import ActionPlanBadge from '@/app/components/action-plan/components/common/action-plan-badge';
import ModalInterventionDetails from '@/app/components/action-plan/components/modals/details/modal-intervention-details';
import { Intervention } from '@/app/components/action-plan/types';
import SeeDetailsButton from '@/components/see-details-button/see-details-button';
import { Checkbox } from '@chakra-ui/react';
import { Flex, Text } from '@cmpsr/components';
import useInterventionCardController from './intervention-card.controller';
import { formatRiskDecimal, normalizeCancerTypeName } from '@/utils';

type CardProps = {
  selectedItems: string[];
  intervention: Intervention;
  onItemSelectionChange: (id: string, checked: boolean) => void;
  isMaxSelectionReached: (id: string) => boolean;
};

export default function InterventionCard({
  selectedItems,
  intervention,
  onItemSelectionChange,
  isMaxSelectionReached,
}: CardProps) {
  const { id, title, isNonQuantifiable, isOpen, onOpen, onClose, reducedRisk, cancerRiskSuffix, otherCancers } =
    useInterventionCardController(intervention);

  return (
    <Flex
      my="spacer-3"
      py="spacer-3"
      px="spacer-4"
      justifyContent="space-between"
      alignItems="center"
      border="1px solid"
      borderColor="ui-element-divider"
      borderRadius="radii-3"
      backgroundColor={isMaxSelectionReached(id) ? 'background-action-disabled' : 'background-light'}
      opacity={isMaxSelectionReached(id) ? 0.5 : 1}
      className='dashboard-reset'
    >
      <Flex direction="column" mr={{ base: 'spacer-4' }}>
        <Text variant="text-body-display-XS" mb="spacer-2">
          {title}
        </Text>
        <Flex
          alignItems={{ base: 'flex-start', md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
          gap={isNonQuantifiable ? { base: 'spacer-2', md: 'spacer-4' } : 'spacer-2'}
        >
          <Text variant="text-body-regular" color="text-secondary">
            {isNonQuantifiable
              ? 'Reduce overall cancer risk'
              : `Reduce ${normalizeCancerTypeName(reducedRisk?.cancerType)}${cancerRiskSuffix} risk by`}
          </Text>
          {isNonQuantifiable ? (
            <SeeDetailsButton onClick={onOpen} />
          ) : (
            <Flex gap="spacer-4">
              <Flex gap="spacer-2">
                <ActionPlanBadge text={formatRiskDecimal(reducedRisk?.impact)} />
                {otherCancers > 0 && (
                  <Text variant="text-body-meta-regular" color="text-secondary">
                    + {otherCancers} others
                  </Text>
                )}
              </Flex>
              <SeeDetailsButton onClick={onOpen} />
            </Flex>
          )}
        </Flex>
      </Flex>
      <Checkbox
        aria-label={`Select ${title}`}
        onChange={(e) => onItemSelectionChange(id, e.target.checked)}
        isChecked={selectedItems.includes(id)}
        size="l"
        isDisabled={isMaxSelectionReached(id)}
      />
      {isOpen && <ModalInterventionDetails isOpen={isOpen} onClose={onClose} intervention={intervention} navToActionPlanHome={false} />}
    </Flex>
  );
}
